import { Command } from "cmdk"
import { useRef, useState } from "react"

import SearchInputChrome from "@molecules/SearchInput/SearchInputChrome/SearchInputChrome"
import { useSelectContext } from "@molecules/Select/Select.context"

import { SelectListSearchProps } from "./SelectListSearch.types"

export default function SelectListSearch(props: SelectListSearchProps) {
    const {
        defaultValue = "",
        isClearable = true,

        placeholder = "Search...",

        autoFocus = true,
        isDisabled = false,

        isLoading: isLoadingProp,
        isSearching: isSearchingProp,
        value: controlledValue,
        onChange,
    } = props

    const { searchData } = useSelectContext()
    const [internalValue, setInternalValue] = useState<string>(defaultValue)
    const inputRef = useRef<HTMLInputElement | null>(null)

    const contextSearchState = searchData?.searchState
    const isControlled = controlledValue !== undefined
    const isContextControlled = !isControlled && contextSearchState !== undefined
    const value = isControlled ? controlledValue : isContextControlled ? contextSearchState.searchTerm : internalValue

    // Until the first results arrive there is nothing to search; after that, a fetch only refreshes the results.
    const isLoading =
        isLoadingProp ?? (searchData !== undefined && searchData.isLoading && !searchData.searchState.hasSearchTerm)
    const isSearching =
        isSearchingProp ??
        (searchData !== undefined &&
            !isLoading &&
            ((searchData.isFetching && !searchData.isFetchingNextPage) || searchData.searchState.isTyping))

    const handleValueChange = (newValue: string) => {
        if (isControlled) {
            onChange?.(newValue)
        } else if (isContextControlled) {
            contextSearchState.onSearchChange(newValue)
            onChange?.(newValue)
        } else {
            setInternalValue(newValue)
            onChange?.(newValue)
        }
    }

    const handleClear = () => {
        handleValueChange("")
        inputRef.current?.focus()
    }

    return (
        <SearchInputChrome
            type="bar"
            renderInput={(className) => (
                <Command.Input
                    ref={inputRef}
                    value={value}
                    onValueChange={handleValueChange}
                    className={className}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    disabled={isDisabled}
                    inputMode="search"
                    enterKeyHint="search"
                />
            )}
            showClearButton={isClearable && value.length > 0}
            onClear={handleClear}
            isFullWidth={true}
            isLoading={isLoading}
            isSearching={isSearching}
            isDisabled={isDisabled}
        />
    )
}
